import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FormCommonApiService } from '../../../../../service/api/formcommon-api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseForm } from '../../../../base-form';
import {
    stack,
    build,
} from '@fitogest/reactive-form-builder/utils';
import { environment } from '../../../../../../environments/environment';
import { ClientesApiService } from '../../../../../service/api/clientes-api.service';
import { AnalisisModel } from '../../../../../models/analisis/analisis.model';
import { AnalisisApiService } from '../../../../../service/api/analisis-api.service';
import { HttpClient } from '@angular/common/http';

import { SiexApiService } from 'src/app/service/api/siex-api.service';
import { AnalisisEnvironmentDefinition } from '../environment-variables';
import { AnalisisFormDefinition } from './fields/formfields';
import { FormPhase, ReactiveFormRequestCoordinator } from '@fitogest/reactive-form-builder/form-request';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComunidadesRegantesModel } from 'src/app/models/analisis/comunidadesRegantes.model';
import { ClienteModel } from 'src/app/models/cliente.model';
import { StorageManager } from 'src/app/common/storage-manager.class';
import {
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
    FormType,
} from '@fitogest/reactive-form-builder/interfaces';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';
const DEFERRED_DROPDOWN_LOAD_DELAY_MS = 0;

@Component({
    selector: 'app-analisis-form',
    standalone: false,
    templateUrl: './analisis-form.component.html',
    styleUrls: ['./analisis-form.component.scss'],
})
export class AnalisisFormComponent extends BaseForm<AnalisisModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de análisis...';

    public comunidadForm: FormGroup;

    /** Reactive form definition bound to the template */
    public form!: AnalisisFormDefinition;

    /** Form request engine (phase-based hooks) */
    public formRequest = new ReactiveFormRequestCoordinator<AnalisisModel>;

    public env: AnalisisEnvironmentDefinition = new AnalisisEnvironmentDefinition();
    public enableFormChanges = true;

    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.analisisApi.analisis.GET;
    public initialPreviousData: AnalisisModel | null = null;

    public reactiveFormRecordId = '';

    public override model = {} as AnalisisModel;
    public type: FormType = this.getType();
    public id_finca = '';

    // public analisisProximo: any;
    public programarSiguiente = false;

    public analisisImg: string | ArrayBuffer | null | undefined = '';

    public showExtraFields = false;

    public tipoSuperficieParcelas: string = environment.features.hasSuperficieCultivada
        ? 'S.\xa0C.'
        : 'S.\xa0Sigpac';
    public oldSector = 0;


    constructor(
        public override route: ActivatedRoute,
        public override router: Router,
        public fincasApi: FincasApiService,
        public clientesApi: ClientesApiService,
        public formApi: FormCommonApiService,
        public override dashboard: DashboardService,
        public siexApi: SiexApiService,
        public analisisApi: AnalisisApiService,
        public http: HttpClient,
        private readonly formBuilder: FormBuilder,
        private readonly cdr: ChangeDetectorRef
    ) {
        super(
            route,
            router,
            dashboard,
            analisisApi.analisis.PUT,
            analisisApi.analisis.POST,
            'analisis',
            'Editar Recordatorio de Análisis',
            'Crear Recordatorio de Análisis'
        );
        this.comunidadForm = this.formBuilder.group({
            nombre: ['', [Validators.required, Validators.maxLength(100)]],
            id_usuario: [StorageManager.getUser().id],
            id_cliente: [StorageManager.getClient()?.id ?? ''],
        });
    }

    public async ngOnInit(): Promise<void> {
        this.registerPersistedAnalisisCallbacks();
        await this.initializeAnalisisForm();
    }


    public stableValueChanges(event: ReactiveFormStableValueChange<AnalisisModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        this.form.model = this.toAnalisisFormValue({
            ...this.form.model,
            ...event.value,
        });
        this.form.tag = event.changedField;
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }


    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<AnalisisModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            if (event.type === 'dropdownDataResolved' || event.type === 'dropdownRefresh') {
                this.refreshReactiveDropdownMappings();
            }

            return;
        }

        this.form.model = this.toAnalisisFormValue({
            ...this.form.model,
            ...event.value,
        });
        this.syncModelFromForm();
        this.refreshReactiveDropdownMappings();
    }

    private ensureSelectedClientOption(): void {
        const selectedClient = StorageManager.getClient();
        const selectedClientId = String(selectedClient?.id ?? '').trim();

        if (!selectedClient || selectedClientId === '') {
            return;
        }

        const exists = (this.form.lists.clientes.values ?? [])
            .some((item) => String((item.value as ClienteModel | null)?.id ?? '').trim() === selectedClientId);

        if (exists) {
            return;
        }

        const option = {
            label: selectedClient.nombre || selectedClient.razon_social || selectedClientId,
            value: selectedClient,
        };

        this.form.lists.clientes.values = [option, ...(this.form.lists.clientes.values ?? [])];
        this.form.lists.clientes.filtered = [option, ...(this.form.lists.clientes.filtered ?? [])];
    }

    private syncSelectedClientControl(): void {
        const selectedClientId = String(StorageManager.getClient()?.id ?? '').trim();
        const control = this.formBuilderRef?.model?.get('id_cliente');

        if (!control || selectedClientId === '') {
            return;
        }

        const currentValue = control.value;
        const hasValue = Array.isArray(currentValue)
            ? currentValue.length > 0
            : String(currentValue ?? '').trim() !== '';

        if (hasValue) {
            return;
        }

        const controlValue = this.form.env.applicationType === 'cropgest' ? [selectedClientId] : selectedClientId;

        control.patchValue(controlValue, { emitEvent: false });
        control.markAsPristine();
    }

    public saveNewOption(): void {
        if (this.comunidadForm.invalid) {
            this.comunidadForm.markAllAsTouched();
            return;
        }

        const endpoint: string = `${this.form.env.serverUrl}/ws/index.php?p1=generic&p2=comunidades_regantes`;
        const payload: ComunidadesRegantesModel = this.comunidadForm.getRawValue();

        this.http.post<ComunidadesRegantesModel>(endpoint, payload)
            .subscribe({
            next: () => {
                this.comunidadForm.reset({
                nombre: '',
                id_usuario: StorageManager.getUser().id,
                id_cliente: StorageManager.getClient()?.id ?? '',
                });

                this.form.openDialog = false;

                stack([
                build(this.analisisApi.comunidadesRegantes.GET, this.form.lists.comunidadesRegantes, 'nombre'),
                ]).then(() => {
                const newName = payload.nombre;
                const match = this.form.lists.comunidadesRegantes.filtered
                    .find((item: any) => item.value && item.value.nombre === newName);

                if (match) {
                    const control = this.formBuilderRef?.model?.get('id_comunidad_regantes');

                    if (control) {
                    control.patchValue(match.value?.id, { emitEvent: false });
                    control.markAsPristine();
                    }
                }

                this.refreshReactiveDropdownMappings();
                return;
                }).catch((e: unknown) => {
                console.error('Error reloading comunidades_regantes:', e);
                });
            },
            error: (error: unknown) => {
                console.error('Error creating comunidad_regantes:', error);
            },
            });
        }


    private async initializeAnalisisForm(): Promise<void> {
        try {
            this.loadingData = true;
            this.isReactiveFormReady = false;
            this.reactiveFormLoadingError = '';
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            const routeModel = this.toAnalisisModel(this.getModel());
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId(routeModel);
            this.model = this.initializeHostModel(routeModel);
            this.initialPreviousData = this.getInitialPreviousData();
            this.form = new AnalisisFormDefinition(
                this.getType(),
                this.model as unknown as Record<string, string>,
                null
            );
            this.form.model = this.toAnalisisFormValue(this.model);
            this.syncModelFromForm();

            await this.initFormData();
            await this.formRequest.executePhase(FormPhase.LOAD);
            this.loadingData = false;
            this.markReactiveFormReady();
            this.scheduleDeferredDropdownCatalogs();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de análisis.', error);
        }
    }


    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        try {
            await this.loadDropdownCatalogPhase('critical');
            this.ensureSelectedClientOption();
            this.syncSelectedClientControl();
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
            throw error;
        }
    }


    private async loadDropdownCatalogPhase(phase: 'critical' | 'deferred'): Promise<void> {
        const catalogFields = this.form.formFields.dropdownCatalogs?.[phase] ?? [];
        const builds = catalogFields
            .map((fieldName) => this.createDropdownCatalogBuild(String(fieldName)))
            .filter((catalogBuild): catalogBuild is (resolve: (ok: boolean) => void) => void => catalogBuild !== null);

        if (builds.length === 0) {
            return;
        }

        await stack(builds);
    }


    private createDropdownCatalogBuild(fieldName: string): ((resolve: (ok: boolean) => void) => void) | null {
        if (fieldName === 'id_cliente') {
            return build(this.clientesApi.clientesGET, this.form.lists.clientes, 'nombre', true);
        }

        if (fieldName === 'id_finca') {
            return build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', true);
        }

        if (fieldName === 'id_sector') {
            return build(this.fincasApi.sectoresGET, this.form.lists.sectores, 'nombre', true);
        }

        if (fieldName === 'ids_parcelas') {
            return build(this.fincasApi.parcelasGET, this.form.lists.lista_parcelas, 'nombre', true);
        }

        if (fieldName === 'id_comunidad_regantes') {
            return build(this.analisisApi.comunidadesRegantes.GET, this.form.lists.comunidadesRegantes, 'nombre');
        }

        if (fieldName === 'parcelas') {
            return build(this.fincasApi.parcelasGET, this.form.lists.parcelas, 'nombre', true);
        }

        return null;
    }


    private scheduleDeferredDropdownCatalogs(): void {
        globalThis.setTimeout((): void => {
            this.loadDropdownCatalogPhase('deferred')
                .then((): void => {
                    this.ensureSelectedClientOption();
                    this.syncSelectedClientControl();
                    this.finalizeExternalDropdowns();
                    this.refreshReactiveDropdownMappings();
                    this.cdr.markForCheck();
                })
                .catch((error: unknown): void => {
                    console.warn('[analisis dropdowns] deferred dropdowns failed.', error);
                });
        }, DEFERRED_DROPDOWN_LOAD_DELAY_MS);
    }


    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }


    private handleUserValueChange(
        formModel: ReactiveFormValue<AnalisisModel>,
        lastChangedFieldName: keyof ReactiveFormValue<AnalisisModel> | string | null,
    ): void {
        const changedFieldName = String(lastChangedFieldName ?? '');

        this.form.model = this.toAnalisisFormValue({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'id_cliente' || changedFieldName === 'id_finca' || changedFieldName === 'id_sector') {
            this.refreshReactiveDropdownMappings();
        }
    }


    private registerPersistedAnalisisCallbacks(): void {
        this.analisisApi.analisis.POST.response((): void => {
            const modelToSave = this.form?.model;
            if (modelToSave?.fecha) {
                localStorage.setItem('analisis_temp', JSON.stringify([modelToSave]));
            }
        });
        this.analisisApi.analisis.PUT.response((): void => {
            const modelToSave = this.form?.model;
            if (modelToSave?.fecha) {
                localStorage.setItem('analisis_temp', JSON.stringify([modelToSave]));
            }
        });
    }


    private getInitialPreviousData(): AnalisisModel | null {
        if (this.getType() !== FormType.DUPLICATION) {
            return null;
        }

        return this.toAnalisisModel(this.model);
    }


    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de análisis: ' + error.message;
        }

        return 'No se ha podido cargar el formulario de análisis. Revisa la conexión e inténtalo de nuevo.';
    }


    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.refreshExternalDropdownMappings();
    }


    private syncModelFromForm(): void {
        const normalizedFormValue = this.toAnalisisFormValue(this.form.model);

        this.form.model = normalizedFormValue;
        this.updateHostModel(this.toAnalisisModel(normalizedFormValue));
    }


    private toAnalisisFormValue(
        sourceModel: ReactiveFormValue<AnalisisModel> | Partial<AnalisisModel> | Record<string, unknown>,
    ): ReactiveFormValue<AnalisisModel> {
        const normalizedModel: Record<string, unknown> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value;
            }
        }

        return normalizedModel as ReactiveFormValue<AnalisisModel>;
    }


    private toAnalisisModel(
        sourceModel: ReactiveFormValue<AnalisisModel> | Partial<AnalisisModel> | Record<string, unknown>,
    ): AnalisisModel {
        return this.toAnalisisFormValue(sourceModel) as unknown as AnalisisModel;
    }
}
